import { abrePDF } from "../../../utils/pdf";
import React, { useState, useEffect, useContext, useMemo } from "react";
import { fDMA } from "../../../utils/fecha";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import {
  Button,
  Row,
  Table,
  Input,
  Label,
  ButtonGroup,
  Col,
} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import moment from "moment";

// Reporte de sólo lectura que unifica Abonos (colección Abonos) con
// Complementos de Pago timbrados (colección CompPago).
function ReporteAbonosCompPago() {
  const { user } = useContext(AuthContext);
  const URL_ABONOS = process.env.REACT_APP_URL_ABONOS;
  const URL_COMP_PAGO = process.env.REACT_APP_URL_COMP_PAGO;
  const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
  const URL_ANTICIPOS = process.env.REACT_APP_URL_ANTICIPOS;

  const hoy = new Date();
  const inicio = new Date();
  inicio.setDate(inicio.getDate() - 30);
  const endDate = moment(hoy).format("YYYY-MM-DD");
  const startDate = moment(inicio).format("YYYY-MM-DD");

  const [selectedFechaInicio, setSelectedFechaInicio] = useState(startDate);
  const [selectedFechaFin, setSelectedFechaFin] = useState(endDate);
  const [selectedCliente, setSelectedCliente] = useState("");
  const [selectedTipo, setSelectedTipo] = useState("");
  const [clientes, setClientes] = useState([]);

  const [comments, setComments] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });

  const ITEMS_PER_PAGE = 50;
  const fmt = (n) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(n) || 0);

  useMemo(() => {
    Promise.all([
      axios.get(`${URL_ABONOS}Fechas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      }),
      axios.get(`${URL_COMP_PAGO}Fechas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      }),
      axios.get(`${URL_ANTICIPOS}Fechas/${selectedFechaInicio}/${selectedFechaFin}`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      }),
    ])
      .then(([resAbonos, resComp, resAnt]) => {
        const abonos = (resAbonos.data || [])
          .filter((a) => a.is_active === "Si")
          .map((a) => ({
            _id: a._id,
            tipo: "Abono",
            fecha: a.fecha,
            numero: a.idAbono,
            clientes: a.clientes?.[0]?.nombre_comercial || "",
            idClientes: a.clientes?.[0]?._id || "",
            banco: a.bancos?.[0]
              ? `${a.bancos[0].banco} ${a.bancos[0].cuenta}`
              : "",
            formaPago: a.formaPago || "",
            concepto: a.concepto || "",
            nota:
              a.surtidos?.[0]?.idSurtido ||
              a.notasCargo?.[0]?.idNotaCargo ||
              a.saldoInicial?.[0]?.idSaldoInicial ||
              "",
            importe: Number(a.importe || 0),
            timbre: "N/A",
          }));
        const comps = (resComp.data || [])
          .filter((c) => c.is_active !== "No")
          .map((c) => ({
            _id: c._id,
            tipo: "Complemento",
            fecha: c.fecha,
            numero: c.idCompPago,
            clientes: c.clientes?.[0]?.nombre_comercial || "",
            idClientes: c.clientes?.[0]?._id || "",
            banco: c.bancos?.[0]
              ? `${c.bancos[0].banco} ${c.bancos[0].cuenta}`
              : "",
            formaPago: c.paymentForm || "",
            concepto: `${c.serie || ""}-${c.folio || ""}`,
            nota: c.folioFiscal || "",
            importe: Number(c.total || 0),
            timbre: c.timbre || "No",
          }));
        // Anticipos: solo lo que falta de aplicar (saldo > 0)
        const anticipos = (resAnt.data || [])
          .filter((a) => a.is_active === "Si" && Number(a.saldo || 0) > 0.01)
          .map((a) => ({
            _id: a._id,
            tipo: "Anticipo",
            fecha: a.fecha,
            numero: a.idAnticipo,
            clientes: a.clientes?.[0]?.nombre_comercial || "",
            idClientes: a.clientes?.[0]?._id || "",
            banco: a.bancos?.[0]
              ? `${a.bancos[0].banco} ${a.bancos[0].cuenta}`
              : "",
            formaPago: a.formaPago || "",
            concepto: `Aplicado ${fmt(Number(a.utilizado || 0))} de ${fmt(Number(a.importe || 0))}`,
            nota: "",
            importe: Number(a.saldo || 0), // solo lo que falta de aplicar
            timbre: "N/A",
          }));
        setComments(
          [...abonos, ...comps, ...anticipos].sort((a, b) =>
            String(b.fecha).localeCompare(String(a.fecha))
          )
        );
      })
      .catch((err) => console.log(err));
  }, [selectedFechaInicio, selectedFechaFin]);

  useEffect(() => {
    axios
      .get(`${URL_CLIENTES}GeneralActivos`, {
        headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
      })
      .then((res) => setClientes(res.data || []))
      .catch((err) => console.log(err));
  }, []);

  const headers = [
    { name: "Fecha", field: "fecha", sortable: true },
    { name: "Tipo", field: "tipo", sortable: true },
    { name: "Num", field: "numero", sortable: true },
    { name: "Cliente", field: "clientes", sortable: true },
    { name: "Banco", field: "banco", sortable: true },
    { name: "Forma Pago", field: "formaPago", sortable: true },
    { name: "Concepto/Folio", field: "concepto", sortable: true },
    { name: "Nota / UUID", field: "nota", sortable: true },
    { name: "Importe", field: "importe", sortable: true },
    { name: "Timbre", field: "timbre", sortable: true },
  ];

  const filtrado = useMemo(() => {
    let c = comments;
    if (search) {
      const s = search.toLowerCase();
      c = c.filter(
        (x) =>
          String(x.clientes || "").toLowerCase().includes(s) ||
          String(x.banco || "").toLowerCase().includes(s) ||
          String(x.concepto || "").toLowerCase().includes(s) ||
          String(x.nota || "").toLowerCase().includes(s) ||
          String(x.formaPago || "").toLowerCase().includes(s) ||
          String(x.importe || "").toString().includes(s)
      );
    }
    if (selectedCliente) c = c.filter((x) => x.idClientes === selectedCliente);
    if (selectedTipo) c = c.filter((x) => x.tipo === selectedTipo);
    setTotalItems(c.length);
    if (sorting.field) {
      const dir = sorting.order === "asc" ? 1 : -1;
      c = [...c].sort((a, b) => {
        const va = a[sorting.field];
        const vb = b[sorting.field];
        if (typeof va === "number") return dir * (va - vb);
        return dir * String(va || "").localeCompare(String(vb || ""));
      });
    }
    return c.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [comments, currentPage, search, sorting, selectedCliente, selectedTipo]);

  const totalImporte = comments.reduce((t, c) => {
    if (selectedCliente && c.idClientes !== selectedCliente) return t;
    if (selectedTipo && c.tipo !== selectedTipo) return t;
    return t + (Number(c.importe) || 0);
  }, 0);

  function PDFTabla() {
    const rows = comments
      .filter((c) => !selectedCliente || c.idClientes === selectedCliente)
      .filter((c) => !selectedTipo || c.tipo === selectedTipo)
      .map((a) => [
        a.fecha,
        a.tipo,
        a.numero,
        a.clientes,
        a.banco,
        a.formaPago,
        a.concepto,
        a.nota,
        fmt(a.importe),
        a.timbre,
      ]);
    const doc = new jsPDF("landscape");
    doc.text(
      `Reporte Abonos + Complementos de Pago  ${selectedFechaInicio} → ${selectedFechaFin}`,
      14,
      14
    );
    doc.autoTable({
      head: [[
        "Fecha", "Tipo", "Num", "Cliente", "Banco", "Forma Pago",
        "Concepto/Folio", "Nota / UUID", "Importe", "Timbre",
      ]],
      body: rows,
      startY: 20,
      foot: [[
        "", "", "", "", "", "", "", "Total", fmt(totalImporte), "",
      ]],
      styles: { fontSize: 8 },
    });
    abrePDF(doc, "ReporteAbonos-CompPago.pdf");
  }

  function excel() {
    const dataExcel = comments
      .filter((c) => !selectedCliente || c.idClientes === selectedCliente)
      .filter((c) => !selectedTipo || c.tipo === selectedTipo)
      .map((a) => ({
        Fecha: a.fecha,
        Tipo: a.tipo,
        Num: a.numero,
        Cliente: a.clientes,
        Banco: a.banco,
        FormaPago: a.formaPago,
        ConceptoFolio: a.concepto,
        NotaUUID: a.nota,
        Importe: Number(a.importe || 0),
        Timbre: a.timbre,
      }));
    const option = {
      fileName: `ReporteAbonos-CompPago-${selectedFechaInicio}-${selectedFechaFin}`,
      datas: [
        {
          sheetData: dataExcel,
          sheetName: "Reporte",
          sheetFilter: [
            "Fecha", "Tipo", "Num", "Cliente", "Banco",
            "FormaPago", "ConceptoFolio", "NotaUUID", "Importe", "Timbre",
          ],
          sheetHeader: [
            "Fecha", "Tipo", "Num", "Cliente", "Banco",
            "Forma Pago", "Concepto/Folio", "Nota/UUID", "Importe", "Timbre",
          ],
        },
      ],
    };
    new ExportJsonExcel(option).saveExcel();
  }

  return (
    <>
      <Header />
      {user.abonos ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/BotonesAdmin"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col sm={4}>
              <ButtonGroup id="logoutBoton">
                <Button size="sm" className="btn" color="danger" onClick={PDFTabla}>
                  PDF <i className="far fa-file-pdf"></i>
                </Button>
                <Button size="sm" className="btn" color="primary" onClick={excel}>
                  Excel <i className="far fa-file-excel"></i>
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
            <h3 align="center">Reporte Abonos + Complementos de Pago</h3>
            <div className="row">
              <div className="col-md-8">
                <Pagination
                  total={totalItems}
                  itemsPerPage={ITEMS_PER_PAGE}
                  currentPage={currentPage}
                  onPageChange={(page) => setCurrentPage(page)}
                />
              </div>
              <div className="col-md-4 d-flex flex-row-reverse">
                <Search
                  onSearch={(value) => {
                    setSearch(value);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>
            <Row>
              <Col md={2}>
                <Label>Fecha Inicio</Label>
                <Input
                  bsSize="sm"
                  type="date"
                  value={selectedFechaInicio}
                  onChange={(e) => setSelectedFechaInicio(e.target.value)}
                />
              </Col>
              <Col md={2}>
                <Label>Fecha Fin</Label>
                <Input
                  bsSize="sm"
                  type="date"
                  value={selectedFechaFin}
                  onChange={(e) => setSelectedFechaFin(e.target.value)}
                />
              </Col>
              <Col md={3}>
                <Label>Cliente</Label>
                <Input
                  bsSize="sm"
                  type="select"
                  value={selectedCliente}
                  onChange={(e) => {
                    setSelectedCliente(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  {clientes
                    .sort((a, b) =>
                      String(a.nombre_comercial || "").localeCompare(
                        String(b.nombre_comercial || "")
                      )
                    )
                    .map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.nombre_comercial}
                      </option>
                    ))}
                </Input>
              </Col>
              <Col md={2}>
                <Label>Tipo</Label>
                <Input
                  bsSize="sm"
                  type="select"
                  value={selectedTipo}
                  onChange={(e) => {
                    setSelectedTipo(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="">Todos</option>
                  <option value="Abono">Abonos</option>
                  <option value="Complemento">Complementos</option>
                  <option value="Anticipo">Anticipos pendientes</option>
                </Input>
              </Col>
              <Col md={3}>
                <Label>Total</Label>
                <Input bsSize="sm" value={fmt(totalImporte)} disabled />
              </Col>
            </Row>
            <br />
            <Table
              size="sm"
              striped
              borderless
              className="table-responsive-xl"
            >
              <TableHeader
                headers={headers}
                onSorting={(field, order) => setSorting({ field, order })}
              />
              <tbody>
                {filtrado.map((a) => (
                  <tr key={a._id}>
                    <td>{fDMA(a.fecha)}</td>
                    <td>
                      <span
                        style={{
                          backgroundColor:
                            a.tipo === "Complemento"
                              ? "#d1e7dd"
                              : a.tipo === "Anticipo"
                              ? "#fff3cd"
                              : "#cfe2ff",
                          padding: "2px 6px",
                          borderRadius: 4,
                        }}
                      >
                        {a.tipo}
                      </span>
                    </td>
                    <td>{a.numero}</td>
                    <td>{a.clientes}</td>
                    <td>{a.banco}</td>
                    <td>{a.formaPago}</td>
                    <td>{a.concepto}</td>
                    <td style={{ fontSize: 10 }}>{a.nota}</td>
                    <td align="right">{fmt(a.importe)}</td>
                    <td>{a.timbre}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
        </div>
      ) : undefined}
    </>
  );
}

export default ReporteAbonosCompPago;
